import { useState, useRef, useEffect, useCallback, type CSSProperties } from 'react';
import { useAnalytics } from '@/shared/features/analytics/presentation/hooks/useAnalytics';
import { useNavigate } from 'react-router-dom';
import { Plus, X } from 'lucide-react';
import bsLogoSymbol from '@/shared/assets/icons/bs-logo-symbol.svg';
import happyShoppingIcon from '@/shared/assets/icons/happy-shopping.svg';
import layoutIcon from '@/shared/assets/icons/layout.svg';
import magniferIcon from '@/shared/assets/icons/Magnifer.svg';
import userIcon from '@/shared/assets/icons/user.svg';
import SearchModal from '@/features/search/presentation/components/SearchModal';
import { navigateToSearchPage } from '@/features/search/utils/searchNavigation';
import HamburgerMenu from '@/shared/features/menu/presentation/components/HamburgerMenu';
import { cn } from '@/shared/utils/cn';
import type { FloatingNavConfig } from '../../domain/models/NewHomePageData';
import { useFloatingNavigation, type FloatingNavStateName } from '../hooks/useFloatingNavigation';
import { useIsMobile, useIsDesktop } from '@/core/hooks/useMediaQuery';
import { getApiBaseUrl } from '@/core/utils/apiUtils';
import { useRevealOnScrollUpWhile } from '@/core/hooks/useRevealOnScrollUpWhile';
import { GoToTopFab } from '@/shared/widgets/GoToTopFab';
import { useUserContext } from '@/shared/features/user-context/presentation/hooks/useUserContext';
import { useAppSelector, useAppDispatch } from '@/app/store/store';
import { openLoginPopup } from '@/app/store/loginUiSlice';
import {
  selectMobileRecommendedFilterStripStuck,
  selectDesktopRecommendedBrowseFilterStripStuck,
  selectRecommendedBrowseFiltersEnabled,
  bumpRecommendedBrowseFilterButton,
} from '@/app/store/newHomeUiSlice';
import slidersIcon from '@/shared/assets/icons/sliders.svg';
import {
  HOME_PAGE_FLOATING_NAV_BAR_CLICKED,
  homePageFloatingNavBarClickedEventProperties,
} from '../analytics/homePageFloatingNavBarClicked';
import { getActiveHomeSectionName } from '../../utils/activeHomeSection';
import {
  HOME_PAGE_FLOATING_LOGO_CLICKED,
  homePageFloatingLogoClickedEventProperties,
  type HomePageFloatingNavLogoState,
} from '../analytics/homePageFloatingLogoClicked';
import { trackSearchedForProduct } from '@/features/search/constants';

interface FloatingNavigationProps {
  config: FloatingNavConfig;
}

const shadowMap: Record<string, string> = {
  sm: 'shadow-sm',
  md: 'shadow-md',
  lg: 'shadow-lg',
  xl: 'shadow-xl',
};

const TOGGLE_ICON_MORPH_MS = 320;
/** When the recommended listing chrome switches to the full-width bar (mobile) */
const MOBILE_STUCK_CHROME_MS = 560;
const MOBILE_STUCK_EASE = 'cubic-bezier(0.32, 0.72, 0, 1)';

function logoFlyQuadraticControl(sx: number, sy: number, ex: number, ey: number): { qx: number; qy: number } {
  const midX = (sx + ex) / 2;
  const midY = (sy + ey) / 2;
  const dx = ex - sx;
  const dy = ey - sy;
  const qx = midX + Math.min(72, Math.abs(dx) * 0.28 + 36);
  const qy = midY - Math.min(160, Math.max(72, Math.abs(dy) * 0.38));
  return { qx, qy };
}

function supportsCssOffsetPath(): boolean {
  if (typeof CSS === 'undefined' || typeof CSS.supports !== 'function') return false;
  try {
    return CSS.supports('offset-path', 'path("M 0 0 Q 1 1 2 2")');
  } catch {
    return false;
  }
}

/**
 * When the nav opens: start on + then transition to X (close) so it reads as one morph, not a hard swap.
 */
function MobileNavIconLabel({ label, useLightIcons }: { label: string; useLightIcons: boolean }) {
  return (
    <span
      className={cn(
        'block max-w-[3.25rem] truncate text-center text-[9px] font-medium leading-none',
        useLightIcons ? 'text-gray-800' : 'text-white/90',
      )}
    >
      {label}
    </span>
  );
}

function ExpandToggleIconMorph() {
  const [showClose, setShowClose] = useState(false);

  useEffect(() => {
    let inner = 0;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => setShowClose(true));
    });
    return () => {
      cancelAnimationFrame(outer);
      cancelAnimationFrame(inner);
    };
  }, []);

  return (
    <div className="relative flex h-[26px] w-[26px] items-center justify-center">
      <Plus
        aria-hidden
        strokeWidth={2.5}
        className={cn(
          'absolute h-5 w-5 text-white ease-out',
          showClose ? 'scale-[0.45] rotate-90 opacity-0' : 'scale-100 rotate-0 opacity-100',
        )}
        style={{ transition: `opacity ${TOGGLE_ICON_MORPH_MS}ms cubic-bezier(0.4, 0, 0.2, 1), transform ${TOGGLE_ICON_MORPH_MS}ms cubic-bezier(0.4, 0, 0.2, 1)` }}
      />
      <X
        aria-hidden
        strokeWidth={2.5}
        className={cn(
          'absolute h-5 w-5 text-white ease-out',
          showClose ? 'scale-100 rotate-0 opacity-100' : 'scale-[0.45] -rotate-90 opacity-0',
        )}
        style={{ transition: `opacity ${TOGGLE_ICON_MORPH_MS}ms cubic-bezier(0.4, 0, 0.2, 1), transform ${TOGGLE_ICON_MORPH_MS}ms cubic-bezier(0.4, 0, 0.2, 1)` }}
      />
    </div>
  );
}

export default function FloatingNavigation({ config }: FloatingNavigationProps) {
  const { analytics, isInitialized } = useAnalytics();
  const { currentState, stateConfig, transition } = useFloatingNavigation(config);
  /** Smooth BS flight only — timing/easing from page JSON (`floatingNav.logoFlyDurationMs`, `logoFlyEasing`). */
  const logoFlyMs = config.logoFlyDurationMs;
  const logoFlyEase = config.logoFlyEasing;
  const isMobile = useIsMobile();
  const isDesktop = useIsDesktop();
  const dispatch = useAppDispatch();
  const handleOpenLogin = useCallback(
    (mode?: 'login' | 'signup') => {
      dispatch(openLoginPopup(mode === 'signup' ? 'signup' : 'otp'));
    },
    [dispatch],
  );
  const { userContext } = useUserContext();
  const navigate = useNavigate();
  const goOrder = useCallback(() => {
    if (config.cartShouldRefresh) window.location.assign('/order');
    else navigate('/order');
  }, [config.cartShouldRefresh, navigate]);
  const trackFloatingNavBarClick = useCallback(
    (buttonName: string) => {
      if (!isInitialized || !analytics) return;
      try {
        const sectionName = getActiveHomeSectionName();
        analytics.trackEvent(
          HOME_PAGE_FLOATING_NAV_BAR_CLICKED,
          homePageFloatingNavBarClickedEventProperties({ buttonName, sectionName }),
        );
      } catch (err) {
        console.error('Error tracking Home Page Floating Nav Bar Clicked:', err);
      }
    },
    [analytics, isInitialized],
  );

  const trackFloatingLogoClick = useCallback(
    (floatingNav: HomePageFloatingNavLogoState) => {
      if (!isInitialized || !analytics) return;
      try {
        analytics.trackEvent(
          HOME_PAGE_FLOATING_LOGO_CLICKED,
          homePageFloatingLogoClickedEventProperties({ floatingNav }),
        );
      } catch (err) {
        console.error('Error tracking Home Page Floating Logo Clicked:', err);
      }
    },
    [analytics, isInitialized],
  );

  const goMyAccount = useCallback(() => {
    let path: string;
    if (userContext?.isLoggedIn) {
      path = '/account.html';
    } else {
      const fromEnv = getApiBaseUrl();
      const redirectBase = (fromEnv && fromEnv !== '/' ? fromEnv : window.location.origin).replace(
        /\/+$/,
        '',
      );
      path = `/showLoginPage?redirectUrl=${encodeURIComponent(`${redirectBase}/`)}`;
    }
    if (config.profileShouldRefresh) window.location.assign(path);
    else navigate(path);
  }, [config.profileShouldRefresh, navigate, userContext?.isLoggedIn]);
  const pillContainerRef = useRef<HTMLDivElement>(null);
  const bsFabRef = useRef<HTMLButtonElement>(null);
  const mobileRecommendedStripStuck = useAppSelector(selectMobileRecommendedFilterStripStuck);
  const desktopRecommendedStripStuck = useAppSelector(selectDesktopRecommendedBrowseFilterStripStuck);
  const recommendedBrowseFiltersEnabled = useAppSelector(selectRecommendedBrowseFiltersEnabled);

  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isManuallyExpanded, setIsManuallyExpanded] = useState(false);
  const [isHintFlipped, setIsHintFlipped] = useState(false);
  const hintFlipStateRef = useRef(false);
  const autoCloseTimerRef = useRef<number | null>(null);
  const pillHideDelayRef = useRef<number | null>(null);
  const [delayedIsHeaderFold, setDelayedIsHeaderFold] = useState(() => currentState === 'expandedCenter');

  // Expansion is entirely manual (tap to open/close).
  // Scroll state only controls visibility — never auto-expands the nav.
  const isExpanded = isManuallyExpanded;

  /** First viewport fold: header visible — show icon pill only (no BS FAB). */
  const isHeaderFold = currentState === 'expandedCenter';
  /** Past header: BS FAB + expandable pill (current behaviour). */
  const isScrolledNav = !isHeaderFold;


  const prevScrollStateRef = useRef<FloatingNavStateName | null>(null);
  const [logoFly, setLogoFly] = useState<{
    startLeft: number;
    startTop: number;
    endLeft: number;
    endTop: number;
    phase: 'start' | 'end';
  } | null>(null);
  const [hideBsFabDuringFly, setHideBsFabDuringFly] = useState(false);

  // Collapse expanded pill when returning to first fold; clear fly state if user scrolls back up mid-animation.
  useEffect(() => {
    if (!isHeaderFold) return;
    queueMicrotask(() => {
      setIsManuallyExpanded(false);
      setLogoFly(null);
      setHideBsFabDuringFly(false);
    });
  }, [isHeaderFold]);

  // Delay hiding the pill by 2 s when leaving the first fold (mobile only).
  useEffect(() => {
    if (pillHideDelayRef.current !== null) {
      clearTimeout(pillHideDelayRef.current);
      pillHideDelayRef.current = null;
    }
    if (isHeaderFold) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- immediate sync when returning to header fold
      setDelayedIsHeaderFold(true);
    } else {
      pillHideDelayRef.current = window.setTimeout(() => {
        pillHideDelayRef.current = null;
        setDelayedIsHeaderFold(false);
      }, 1000);
    }
    return () => {
      if (pillHideDelayRef.current !== null) {
        clearTimeout(pillHideDelayRef.current);
        pillHideDelayRef.current = null;
      }
    };
  }, [isHeaderFold]);

  // BS mark flies from header logo anchor → bottom-right FAB when leaving first fold (mobile new home only).
  useEffect(() => {
    if (!isMobile) return;

    const prev = prevScrollStateRef.current;
    prevScrollStateRef.current = currentState;

    if (prev === null || prev !== 'expandedCenter' || currentState === 'expandedCenter') return;

    const anchor = document.getElementById('nh-header-logo-anchor');
    const vw = typeof window !== 'undefined' ? window.innerWidth : 0;
    const vh = typeof window !== 'undefined' ? window.innerHeight : 0;
    // FAB: left edge calc(100vw - 72px), circle 56×56 → center (vw - 44, vh - 44) with bottom-4 (16px)
    const endLeft = vw - 44;
    const endTop = vh - 44;

    if (!anchor || vw === 0) {
      return;
    }

    const r = anchor.getBoundingClientRect();
    const startLeft = r.left + r.width / 2;
    const startTop = r.top + r.height / 2;

    let cancelled = false;
    queueMicrotask(() => {
      if (cancelled) return;
      setHideBsFabDuringFly(true);
      setLogoFly({
        startLeft,
        startTop,
        endLeft,
        endTop,
        phase: 'start',
      });
    });

    const raf = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!cancelled) setLogoFly((s) => (s ? { ...s, phase: 'end' } : null));
      });
    });

    const done = window.setTimeout(() => {
      if (!cancelled) {
        setLogoFly(null);
        setHideBsFabDuringFly(false);
      }
    }, logoFlyMs);

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      window.clearTimeout(done);
      // Strict Mode re-runs this effect: the timeout never fires, so we must reset here or the FAB stays visibility:hidden and ignores clicks.
      setHideBsFabDuringFly(false);
      setLogoFly(null);
    };
  }, [currentState, isMobile, logoFlyMs]);

  // In closed state: periodically flip between BS and + as a visual hint (only after scroll — BS FAB visible).
  // BS shows for 3 s → + for 2 s → repeat. When opened, + animates into X (see ExpandToggleIconMorph).
  useEffect(() => {
    if (isManuallyExpanded || isHeaderFold) {
      hintFlipStateRef.current = false;
      queueMicrotask(() => {
        setIsHintFlipped(false);
      });
      return;
    }

    let timeoutId: number;
    const tick = () => {
      hintFlipStateRef.current = !hintFlipStateRef.current;
      setIsHintFlipped(hintFlipStateRef.current);
      // + visible for 2 s, BS visible for 3 s
      timeoutId = window.setTimeout(tick, hintFlipStateRef.current ? 2000 : 3000);
    };

    // Show BS first for 3 s, then start alternating
    timeoutId = window.setTimeout(tick, 3000);
    return () => window.clearTimeout(timeoutId);
  }, [isManuallyExpanded, isHeaderFold]);

  // Resets (or starts) the 5-second inactivity auto-close timer.
  // Called on open and on every pointer interaction inside the nav.
  const resetAutoCloseTimer = () => {
    if (autoCloseTimerRef.current !== null) window.clearTimeout(autoCloseTimerRef.current);
    autoCloseTimerRef.current = window.setTimeout(() => setIsManuallyExpanded(false), 5000);
  };

  // Start the auto-close timer when nav opens; clear it when it closes
  useEffect(() => {
    if (!isManuallyExpanded) {
      if (autoCloseTimerRef.current !== null) {
        window.clearTimeout(autoCloseTimerRef.current);
        autoCloseTimerRef.current = null;
      }
      return;
    }
    resetAutoCloseTimer();
    return () => {
      if (autoCloseTimerRef.current !== null) window.clearTimeout(autoCloseTimerRef.current);
    };
  }, [isManuallyExpanded]);

  // Click outside collapses the manually expanded nav
  useEffect(() => {
    if (!isManuallyExpanded) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      const insidePill = pillContainerRef.current?.contains(target) ?? false;
      const insideBs = bsFabRef.current?.contains(target) ?? false;
      if (!insidePill && !insideBs) setIsManuallyExpanded(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isManuallyExpanded]);

  // GoToTop is shown on both mobile and desktop whenever the category strip is stuck —
  // independent of showInMobile / showInDesktop (which only gate the full floating nav).
  const goToTopEnabled =
    config.enabled && (mobileRecommendedStripStuck || desktopRecommendedStripStuck);
  const goToTopOpen = useRevealOnScrollUpWhile(goToTopEnabled);

  // Visibility checks for the full floating nav
  if (isMobile && !config.showInMobile) return null;
  if (!config.enabled) return null;

  // Desktop: floating nav is disabled but we still want the GoToTop FAB.
  if (isDesktop && !config.showInDesktop) {
    return (
      <GoToTopFab
        open={goToTopOpen}
        zIndexClassName="z-[54]"
        offsetBottomClassName="bottom-8"
      />
    );
  }

  const { style } = stateConfig;
  const { chrome } = config;
  const mobileStuck = isMobile && mobileRecommendedStripStuck;
  const dur = transition.duration;
  const ease = transition.easing;

  const navIconStyle: CSSProperties = chrome.useLightIcons
    ? { filter: 'brightness(0)' }
    : { filter: 'brightness(0) invert(1)' };

  const glassBarStyle: CSSProperties = {
    background: chrome.glassBackground,
    backdropFilter: 'blur(16px) saturate(180%)',
    WebkitBackdropFilter: 'blur(16px) saturate(180%)',
    border: chrome.glassBarBorder,
  };

  /** First fold: honour JSON toggles on expandedCenter; after scroll, all icons appear when pill is opened. */
  const showMenuBtn = isHeaderFold ? stateConfig.showMenu : true;
  const showCartBtn = isHeaderFold ? stateConfig.showCart : true;
  const showSearchBtn = isHeaderFold ? stateConfig.showSearch : true;

  const pillDockedOpen = delayedIsHeaderFold;
  const pillOpen = pillDockedOpen || isExpanded;
  /** Mobile: max width keeps centered pill clear of fixed BS FAB (56px @ right-4 + gap); first fold has no BS. */
  const pillW = pillOpen
    ? isMobile
      ? isHeaderFold
        ? 'min(280px, calc(100vw - 2rem - env(safe-area-inset-left, 0px) - env(safe-area-inset-right, 0px)))'
        : 'min(280px, calc(100vw - 2rem - env(safe-area-inset-left, 0px) - env(safe-area-inset-right, 0px)), calc(100vw - 160px - 2 * env(safe-area-inset-right, 0px)))'
      : '220px'
    : '0';
  const pillOp = pillOpen ? 1 : 0;

  const handleSearch = (query: string) => {
    const trimmed = query.trim();
    if (!trimmed) return;
    trackSearchedForProduct(analytics, trimmed);
    navigateToSearchPage(query, (url) => navigate(url, { replace: false }));
    setIsSearchModalOpen(false);
  };

  return (
    <>
      {/* Mobile: full-width glass bar — slides up when category strip is stuck; 24px top radius (Figma) */}
      {isMobile && (
        <div
          className={cn(
            'fixed bottom-0 left-0 right-0 z-[52] will-change-transform max-md:pb-[env(safe-area-inset-bottom)]',
            'motion-reduce:transition-none',
            mobileStuck
              ? 'translate-y-0 pointer-events-auto'
              : 'translate-y-full pointer-events-none',
          )}
          style={{
            transition: `transform ${MOBILE_STUCK_CHROME_MS}ms ${MOBILE_STUCK_EASE}`,
          }}
          aria-hidden={!mobileStuck}
        >
          <div
            className={cn(
              'flex w-full items-stretch gap-2 overflow-hidden rounded-t-[24px] px-2 py-1.5 sm:gap-2 sm:px-2',
              shadowMap[style.shadow] ?? 'shadow-lg',
            )}
            style={glassBarStyle}
          >
            {stateConfig.showMenu && (
              <button
                type="button"
                onClick={() => {
                  trackFloatingNavBarClick('More');
                  setIsMenuOpen(true);
                }}
                className="flex min-h-14 min-w-0 flex-1 flex-col items-center justify-center gap-1"
                aria-label="More"
              >
                <img src={layoutIcon} alt="" className="h-8 w-8 shrink-0" style={navIconStyle} />
                <MobileNavIconLabel label="More" useLightIcons={chrome.useLightIcons} />
              </button>
            )}

            {stateConfig.showCart && (
              <button
                type="button"
                onClick={() => {
                  trackFloatingNavBarClick('Cart');
                  goOrder();
                }}
                className="relative flex min-h-14 min-w-0 flex-1 flex-col items-center justify-center gap-1"
                aria-label="Cart"
              >
                <span className="relative inline-flex shrink-0">
                  <img src={happyShoppingIcon} alt="" className="h-8 w-8" style={navIconStyle} />
                  {(userContext?.cartCount ?? 0) > 0 && (
                    <span className="absolute -top-0.5 -right-1 flex h-3.5 min-w-[14px] items-center justify-center rounded-full bg-white/80 px-0.5 text-[9px] font-semibold text-gray-900">
                      {userContext?.cartCount}
                    </span>
                  )}
                </span>
                <MobileNavIconLabel label="Cart" useLightIcons={chrome.useLightIcons} />
              </button>
            )}

            {stateConfig.showSearch && (
              <button
                type="button"
                onClick={() => {
                  trackFloatingNavBarClick('Search');
                  setIsSearchModalOpen(true);
                }}
                className="flex min-h-14 min-w-0 flex-1 flex-col items-center justify-center gap-1"
                aria-label="Search"
              >
                <img src={magniferIcon} alt="" className="h-8 w-8 shrink-0" style={navIconStyle} />
                <MobileNavIconLabel label="Search" useLightIcons={chrome.useLightIcons} />
              </button>
            )}

            <button
              type="button"
              onClick={() => {
                trackFloatingNavBarClick('Profile');
                goMyAccount();
              }}
              className="flex min-h-14 min-w-0 flex-1 flex-col items-center justify-center gap-1"
              aria-label="Profile"
            >
              <img src={userIcon} alt="" className="h-8 w-8 shrink-0" style={navIconStyle} />
              <MobileNavIconLabel label="Profile" useLightIcons={chrome.useLightIcons} />
            </button>

            <button
              type="button"
              onClick={() => dispatch(bumpRecommendedBrowseFilterButton())}
              className="flex min-h-14 min-w-0 flex-1 flex-col items-center justify-center gap-1 disabled:opacity-40"
              disabled={!recommendedBrowseFiltersEnabled}
              aria-label="Filters"
            >
              <img src={slidersIcon} alt="" className="h-8 w-8 shrink-0" style={navIconStyle} />
              <MobileNavIconLabel label="Filters" useLightIcons={chrome.useLightIcons} />
            </button>
          </div>
        </div>
      )}

      <GoToTopFab
        open={goToTopOpen}
        zIndexClassName="z-[54]"
        offsetBottomClassName="bottom-[calc(1rem+5.5rem+env(safe-area-inset-bottom,0px))] md:bottom-8"
      />

      {/* Flying BS from header logo → FAB (one-shot on leaving first fold; curved path when CSS supports offset-path) */}
      {logoFly &&
        (() => {
          const sx = logoFly.startLeft;
          const sy = logoFly.startTop;
          const ex = logoFly.endLeft;
          const ey = logoFly.endTop;
          const { qx, qy } = logoFlyQuadraticControl(sx, sy, ex, ey);
          const curved = supportsCssOffsetPath();
          const path = `path('M ${sx} ${sy} Q ${qx} ${qy} ${ex} ${ey}')`;

          return (
            <div
              className={cn(
                'fixed z-[60] pointer-events-none flex items-center justify-center rounded-full w-14 h-14',
                shadowMap[style.shadow] ?? 'shadow-lg',
                'bg-[var(--brand-color)] border border-white/15',
              )}
              style={
                curved
                  ? {
                      left: 0,
                      top: 0,
                      offsetPath: path,
                      offsetDistance: logoFly.phase === 'end' ? '100%' : '0%',
                      offsetRotate: '0deg',
                      offsetAnchor: 'center center',
                      transition:
                        logoFly.phase === 'end'
                          ? `offset-distance ${logoFlyMs}ms ${logoFlyEase}`
                          : 'none',
                      backdropFilter: 'blur(12px)',
                      WebkitBackdropFilter: 'blur(12px)',
                    }
                  : {
                      left: logoFly.phase === 'end' ? logoFly.endLeft - 28 : logoFly.startLeft - 28,
                      top: logoFly.phase === 'end' ? logoFly.endTop - 28 : logoFly.startTop - 28,
                      transition:
                        logoFly.phase === 'end'
                          ? `left ${logoFlyMs}ms ${logoFlyEase}, top ${logoFlyMs}ms ${logoFlyEase}`
                          : 'none',
                      backdropFilter: 'blur(12px)',
                      WebkitBackdropFilter: 'blur(12px)',
                    }
              }
              aria-hidden
            >
              <img
                src={bsLogoSymbol}
                alt=""
                className="h-auto w-[26px]"
                style={{ filter: 'brightness(0) invert(1)' }}
              />
            </div>
          );
        })()}

      {/* Glass pill — horizontally centered; BS FAB is a separate fixed control (bottom-right). */}
      <div
        className={cn(
          'fixed bottom-4 left-0 right-0 z-50 flex justify-center',
          isMobile && 'will-change-transform motion-reduce:transform-none',
          isMobile && (mobileStuck ? 'pointer-events-none translate-x-[120vw] opacity-0' : 'translate-x-0 opacity-100'),
        )}
        style={
          isMobile
            ? { transition: `transform ${MOBILE_STUCK_CHROME_MS}ms ${MOBILE_STUCK_EASE}, opacity ${MOBILE_STUCK_CHROME_MS}ms ${MOBILE_STUCK_EASE}` }
            : undefined
        }
      >
        <div
          ref={pillContainerRef}
          onPointerEnter={!mobileStuck && isManuallyExpanded ? resetAutoCloseTimer : undefined}
          onPointerMove={!mobileStuck && isManuallyExpanded ? resetAutoCloseTimer : undefined}
          className={cn(isMobile && mobileStuck && 'pointer-events-none')}
        >
          {/* Glass pill — docked open on first fold; opens in place when expanded */}
          <div
            className={cn(
              'flex rounded-full overflow-hidden',
              isMobile && pillOpen ? 'items-stretch' : 'items-center justify-center',
              shadowMap[style.shadow] ?? 'shadow-lg',
            )}
            style={{
              height: isMobile && pillOpen ? 'auto' : '56px',
              minHeight: isMobile && pillOpen ? '56px' : undefined,
              width: pillW,
              opacity: pillOp,
              background: chrome.glassBackground,
              backdropFilter: 'blur(16px) saturate(180%)',
              WebkitBackdropFilter: 'blur(16px) saturate(180%)',
              border: pillOpen ? chrome.glassPillBorderOpen : 'none',
              pointerEvents: pillOpen ? 'auto' : 'none',
              /* After scroll: slide from BS FAB (bottom-right) into center while opening; reverse on close. */
              transform:
                delayedIsHeaderFold || isExpanded
                  ? 'translateX(0)'
                  : 'translateX(calc(50vw - 1rem - 28px - env(safe-area-inset-right, 0px)))',
              transition: `width ${dur}ms ${ease}, opacity ${dur}ms ${ease}, transform ${dur}ms ${ease}`,
            }}
          >
            <div
              className={cn(
                'flex items-center',
                isMobile ? 'w-full gap-2 px-2 py-1.5' : 'gap-[18px] px-5',
              )}
            >
              {showMenuBtn && (
                <button
                  type="button"
                  onClick={() => {
                    trackFloatingNavBarClick('More');
                    setIsMenuOpen(true);
                  }}
                  className={cn(
                    'flex items-center justify-center',
                    isMobile ? 'min-w-0 flex-1 flex-col gap-1' : '',
                  )}
                  aria-label="More"
                >
                  <img src={layoutIcon} alt="" className="h-8 w-8 shrink-0" style={navIconStyle} />
                  {isMobile && <MobileNavIconLabel label="More" useLightIcons={chrome.useLightIcons} />}
                </button>
              )}

              {showCartBtn && (
                <button
                  type="button"
                  onClick={() => {
                    trackFloatingNavBarClick('Cart');
                    goOrder();
                  }}
                  className={cn(
                    'flex items-center justify-center',
                    isMobile ? 'min-w-0 flex-1 flex-col gap-1' : '',
                  )}
                  aria-label="Cart"
                >
                  <span className="relative inline-flex shrink-0">
                    <img src={happyShoppingIcon} alt="" className="h-8 w-8" style={navIconStyle} />
                    {(userContext?.cartCount ?? 0) > 0 && (
                      <span className="absolute -top-1 -right-1 flex h-3.5 min-w-[14px] items-center justify-center rounded-full bg-white/80 px-0.5 text-[9px] font-semibold text-gray-900">
                        {userContext?.cartCount}
                      </span>
                    )}
                  </span>
                  {isMobile && <MobileNavIconLabel label="Cart" useLightIcons={chrome.useLightIcons} />}
                </button>
              )}

              {showSearchBtn && (
                <button
                  type="button"
                  onClick={() => {
                    trackFloatingNavBarClick('Search');
                    setIsSearchModalOpen(true);
                  }}
                  className={cn(
                    'flex items-center justify-center',
                    isMobile ? 'min-w-0 flex-1 flex-col gap-1' : '',
                  )}
                  aria-label="Search"
                >
                  <img src={magniferIcon} alt="" className="h-8 w-8 shrink-0" style={navIconStyle} />
                  {isMobile && <MobileNavIconLabel label="Search" useLightIcons={chrome.useLightIcons} />}
                </button>
              )}

              <button
                type="button"
                onClick={() => {
                  trackFloatingNavBarClick('Profile');
                  goMyAccount();
                }}
                className={cn(
                  'flex items-center justify-center',
                  isMobile ? 'min-w-0 flex-1 flex-col gap-1' : '',
                )}
                aria-label="Profile"
              >
                <img src={userIcon} alt="" className="h-8 w-8 shrink-0" style={navIconStyle} />
                {isMobile && <MobileNavIconLabel label="Profile" useLightIcons={chrome.useLightIcons} />}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* BS toggle — fixed bottom-right; does not move when the centered pill opens */}
      {isScrolledNav && (
        <button
          ref={bsFabRef}
          type="button"
          onClick={() => {
            trackFloatingLogoClick(isManuallyExpanded ? 'Closed' : 'Opened');
            setIsManuallyExpanded((v) => !v);
          }}
          onPointerEnter={!mobileStuck && isManuallyExpanded ? resetAutoCloseTimer : undefined}
          onPointerMove={!mobileStuck && isManuallyExpanded ? resetAutoCloseTimer : undefined}
          className={cn(
            'fixed bottom-4 right-4 z-50 flex h-14 w-14 shrink-0 items-center justify-center rounded-full motion-reduce:transform-none',
            shadowMap[style.shadow] ?? 'shadow-lg',
            'bg-[var(--brand-color)]',
            isMobile && 'will-change-transform',
            isMobile && (mobileStuck ? 'pointer-events-none translate-x-[120vw] opacity-0' : 'translate-x-0 opacity-100'),
          )}
          style={{
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            border: '1px solid rgba(255,255,255,0.15)',
            perspective: '200px',
            visibility: hideBsFabDuringFly ? 'hidden' : 'visible',
            ...(isMobile
              ? { transition: `transform ${MOBILE_STUCK_CHROME_MS}ms ${MOBILE_STUCK_EASE}, opacity ${MOBILE_STUCK_CHROME_MS}ms ${MOBILE_STUCK_EASE}` }
              : {}),
          }}
          aria-label={isExpanded ? 'Collapse navigation' : 'Open navigation'}
        >
          {isManuallyExpanded ? (
            <ExpandToggleIconMorph />
          ) : (
            /* 3-D flip: BS front, + back (tap to open hint) */
            <div
              style={{
                position: 'relative',
                width: '26px',
                height: '26px',
                transformStyle: 'preserve-3d',
                transform: isHintFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
                transition: 'transform 420ms cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  backfaceVisibility: 'hidden',
                  WebkitBackfaceVisibility: 'hidden',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <img
                  src={bsLogoSymbol}
                  alt="BlueStone"
                  className="h-auto w-[26px]"
                  style={{ filter: 'brightness(0) invert(1)' }}
                />
              </div>
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  backfaceVisibility: 'hidden',
                  WebkitBackfaceVisibility: 'hidden',
                  transform: 'rotateY(180deg)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Plus className="h-5 w-5 text-white" strokeWidth={2.5} />
              </div>
            </div>
          )}
        </button>
      )}

      {/* Search Modal */}
      <SearchModal
        isOpen={isSearchModalOpen}
        onClose={() => setIsSearchModalOpen(false)}
        onSearch={handleSearch}
      />

      {/* Hamburger Menu */}
      <HamburgerMenu
        isOpen={isMenuOpen}
        onClose={() => setIsMenuOpen(false)}
        onOpenLogin={handleOpenLogin}
      />
    </>
  );
}
